import React, { useContext } from 'react';

import {
  LinearProgress,
  Button,
  Divider,
  InputLabel,
  Typography,
} from '@material-ui/core';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import { TextField } from 'formik-material-ui';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { CardTitle } from 'app/components/shared/card-title';
import { OrganizationExtraFields } from 'app/configuration/organizations/organization-extra-fields';
import {
  OrganizationFormState,
  validateOrganizationForm,
} from 'app/configuration/organizations/organization-helpers';
import { DIRECTIONS } from 'app/i18n-locale/locales-constants';
import { useStyles } from 'app/operations/drivers/driver-form';
import { LanguageContext } from 'context/intl.context';

interface OrganizationForm {
  initialState: OrganizationFormState;
  handleSubmit: (
    values: OrganizationFormState,
    { setSubmitting }: FormikHelpers<OrganizationFormState>
  ) => void;
  isEditForm?: boolean;
  // Extra details (Express API) shown under the Ruby inputs. 'hidden' on the
  // add screen: there they are collected in a modal after the Ruby add succeeds.
  extraDetailsStatus?: 'hidden' | 'loading' | 'error' | 'ready';
  // edit of a legacy org that has neither is_pto nor my_company may leave it as is
  allowEmptyKind?: boolean;
}

const OrganizationForm: React.FC<OrganizationForm> = ({
  initialState,
  handleSubmit,
  isEditForm = false,
  extraDetailsStatus = 'hidden',
  allowEmptyKind = false,
}) => {
  const history = useHistory();
  const classes = useStyles();
  const intl = useIntl();

  const { direction } = useContext(LanguageContext);

  const isRTL = direction === DIRECTIONS.RTL;

  return (
    <>
      <CardTitle
        title={intl.formatMessage({
          id: isEditForm ? 'editOrganization' : 'createOrganization',
        })}
      />

      <div className={classes.formWrapper}>
        <Formik
          initialValues={initialState}
          validate={values =>
            validateOrganizationForm(values, intl, allowEmptyKind)
          }
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form>
              <div
                className={classes.fieldGroupWrapper}
                style={{ flexDirection: 'column' }}
              >
                <div className={classes.fieldWrapper}>
                  <InputLabel
                    className={classes.formLabel}
                    style={{ flexDirection: isRTL ? 'row' : 'row-reverse' }}
                  >
                    <strong className={classes.requiredAstrisk}>*</strong>

                    {intl.formatMessage({ id: 'englishName' })}
                  </InputLabel>

                  <Field
                    name="englishName"
                    component={TextField}
                    type="text"
                    variant="outlined"
                    size="small"
                  />
                </div>

                <div className={classes.fieldWrapper}>
                  <InputLabel
                    className={classes.formLabel}
                    style={{ flexDirection: isRTL ? 'row' : 'row-reverse' }}
                  >
                    <strong className={classes.requiredAstrisk}>*</strong>

                    {intl.formatMessage({ id: 'arabicName' })}
                  </InputLabel>

                  <Field
                    name="arabicName"
                    component={TextField}
                    type="text"
                    variant="outlined"
                    size="small"
                  />
                </div>
              </div>

              {extraDetailsStatus !== 'hidden' && (
                <>
                  <Divider style={{ margin: '8px 0 16px' }} />

                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 700, marginBottom: 16 }}
                  >
                    {intl.formatMessage({ id: 'orgAdditionalDetails' })}
                  </Typography>

                  {extraDetailsStatus === 'loading' && <LinearProgress />}

                  {extraDetailsStatus === 'error' && (
                    <Typography color="error" variant="body2">
                      {intl.formatMessage({ id: 'orgExtraDetailsLoadFailed' })}
                    </Typography>
                  )}

                  {extraDetailsStatus === 'ready' && (
                    <OrganizationExtraFields disabled={isSubmitting} />
                  )}
                </>
              )}

              {isSubmitting && <LinearProgress />}

              <div className={classes.buttonsWrapper}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className={`${classes.submitButton} ${classes.actionButton}`}
                >
                  {intl.formatMessage({ id: 'save' })}
                </Button>

                <Button
                  variant="outlined"
                  color="default"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => history.go(-1)}
                  className={classes.actionButton}
                >
                  {intl.formatMessage({ id: 'cancel' })}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export { OrganizationForm };
